import { apiClient } from "./authService";
import type { VideoPlaybackKind } from "../utils/videoPlayback";

export interface PublicCourseCard {
  _id: string;
  /** English title — always present. Use `localizedTitle()` to render. */
  title: string;
  /** Arabic title — empty string when not translated yet. */
  titleAr?: string;
  description: string;
  descriptionAr?: string;
  thumbnail: string;
  status: "published";
  sessionType?: "group" | "private" | null;
  phaseCount: number;
  itemCount: number;
  videoCount: number;
  /** Leaflets in this course that carry a paid companion video. */
  companionVideoCount?: number;
  pdfCount: number;
  letterCount: number;
  commentCount: number;
  likeCount: number;
  likedByMe: boolean;
  estimatedDuration: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * A leaflet as the storefront shows it before purchase — sales copy and counts
 * only. Served without authentication, so it never carries file or video URLs.
 */
export interface LeafletCatalogItem {
  _id: string;
  title: string;
  titleAr?: string;
  description: string;
  descriptionAr?: string;
  thumbnail: string;
  itemCount: number;
  pdfCount: number;
  /** Total PDF pages across the leaflet. */
  pageCount: number;
  /** Leaflets in this course that carry a paid companion video. */
  companionVideoCount: number;
}

export interface PublicCourseItemVideo {
  _id: string;
  title: string;
  duration: number;
  status: string;
  thumbnailUrl: string;
  cloudflareVideoId: string;
  /** Which player this video needs — resolved server-side. */
  playbackKind: VideoPlaybackKind;
  /** Cloudflare Stream embed; empty unless `playbackKind` is "stream". */
  iframeUrl: string;
  /** Direct media URL; empty unless `playbackKind` is "file". */
  playbackUrl: string;
}

/**
 * Companion video attached to a PDF lesson — a paid add-on.
 *
 * A member without the entitlement still gets the title, duration and poster so
 * the upsell can be shown, but the server withholds everything that could play
 * it — `playbackKind` is "none" and every URL comes back empty — and flags the
 * item with `locked`.
 */
export interface PublicCourseItemCompanionVideo {
  _id: string;
  title: string;
  duration: number;
  status: string;
  thumbnailUrl: string;
  locked: boolean;
  cloudflareVideoId: string;
  playbackKind: VideoPlaybackKind;
  iframeUrl: string;
  playbackUrl: string;
}

export interface SurveyQuestion {
  id: string;
  text: string;
  type: "radio" | "checkbox" | "text" | "dropdown";
  options: string[];
  correctAnswer?: string | string[];
  required: boolean;
}

export interface SurveyConfig {
  mode: "survey" | "quiz";
  description?: string;
  descriptionAr?: string;
  passingScore?: number;
  questions: SurveyQuestion[];
}

export interface PublicCourseItem {
  _id: string;
  type: "video" | "pdf" | "letter" | "survey";
  title: string;
  titleAr?: string;
  order: number;
  pdfUrl?: string;
  pdfUrlAr?: string;
  pdfPages?: number;
  letterContent?: string;
  letterContentAr?: string;
  surveyConfig?: SurveyConfig;
  video?: PublicCourseItemVideo | null;
  /** PDF items only — the paid video that goes with the leaflet. */
  companionVideo?: PublicCourseItemCompanionVideo | null;
}

export interface PublicCoursePhase {
  _id: string;
  title: string;
  titleAr?: string;
  order: number;
  items: PublicCourseItem[];
}

export interface ZoomMeetingRef {
  _id: string;
  topic: string;
  startTime: string;
  joinUrl: string;
  zoomMeetingId: string;
  status: string;
}

export interface PublicCourseDetail {
  _id: string;
  title: string;
  titleAr?: string;
  description: string;
  descriptionAr?: string;
  thumbnail: string;
  status: "published";
  phases: PublicCoursePhase[];
  zoomMeeting?: ZoomMeetingRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface CourseStats {
  phaseCount: number;
  itemCount: number;
  videoCount: number;
  pdfCount: number;
  letterCount: number;
  estimatedDuration: number;
}

export interface CoursesResponse {
  courses: PublicCourseCard[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface CourseDetailResponse {
  course: PublicCourseDetail;
  stats: CourseStats;
  access?: {
    isLocked: boolean;
    lockReason?: string;
    /** The member paid for this course's companion videos. */
    companionVideoUnlocked?: boolean;
    /**
     * The member owns the course but not its companion video, and the course
     * has one — so the video can be bought on its own.
     */
    canBuyCompanionVideo?: boolean;
  };
  progress?: {
    completedItemIds: string[];
    progressPercentage: number;
  };
  engagement?: {
    commentCount: number;
    likeCount: number;
    likedByMe: boolean;
  };
}

export interface CourseCommentUser {
  _id: string;
  name: string;
  avatar?: string;
}

export interface CourseComment {
  _id: string;
  courseId: string;
  parentCommentId: string | null;
  content: string;
  createdAt: string;
  updatedAt: string;
  user: CourseCommentUser;
  likeCount: number;
  likedByMe: boolean;
  replyCount?: number;
  replies?: CourseComment[];
}

export interface CourseCommentsResponse {
  comments: CourseComment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export const coursesService = {
  async getCourses(params?: {
    search?: string;
    page?: number;
    limit?: number;
    sessionType?: "group" | "private";
  }): Promise<CoursesResponse> {
    const response = await apiClient.get("/courses", { params });
    return response.data;
  },

  /** Public — works for visitors who have not signed up yet. */
  async getCatalog(): Promise<{ courses: LeafletCatalogItem[] }> {
    const response = await apiClient.get("/courses/catalog");
    return response.data;
  },

  async getCourse(id: string): Promise<CourseDetailResponse> {
    const response = await apiClient.get(`/courses/${id}`);
    return response.data;
  },

  async updateCourseProgress(
    id: string,
    completedItemIds: string[]
  ): Promise<{
    completedItemIds: string[];
    progressPercentage: number;
    isLocked: boolean;
    lockReason?: string;
  }> {
    const response = await apiClient.patch(`/courses/${id}/progress`, {
      completedItemIds,
    });
    return response.data;
  },

  async getCourseComments(
    id: string,
    params?: { page?: number; limit?: number }
  ): Promise<CourseCommentsResponse> {
    const response = await apiClient.get(`/courses/${id}/comments`, { params });
    return response.data;
  },

  async createCourseComment(
    id: string,
    payload: { content: string; parentCommentId?: string }
  ): Promise<{ comment: CourseComment }> {
    const response = await apiClient.post(`/courses/${id}/comments`, payload);
    return response.data;
  },

  async toggleCourseCommentLike(
    id: string,
    commentId: string
  ): Promise<{ commentId: string; likedByMe: boolean; likeCount: number }> {
    const response = await apiClient.patch(`/courses/${id}/comments/${commentId}/like`);
    return response.data;
  },

  async deleteCourseComment(
    id: string,
    commentId: string
  ): Promise<{ success: boolean; deletedCommentId: string }> {
    const response = await apiClient.delete(`/courses/${id}/comments/${commentId}`);
    return response.data;
  },

  async toggleCourseLike(
    id: string
  ): Promise<{ courseId: string; likedByMe: boolean; likeCount: number }> {
    const response = await apiClient.patch(`/courses/${id}/like`);
    return response.data;
  },

  async submitSurvey(
    courseId: string,
    itemId: string,
    answers: Record<string, string | string[]>
  ): Promise<{ score?: number; passed?: boolean; mode: string; message?: string; correctAnswers?: number; totalQuestions?: number }> {
    const response = await apiClient.post(
      `/courses/${courseId}/items/${itemId}/survey-submit`,
      { answers }
    );
    return response.data;
  },

  async getPdfSignedUrl(
    key: string,
    mode: "view" | "download" = "view"
  ): Promise<{ url: string }> {
    const response = await apiClient.get("/courses/pdf-signed-url", {
      params: { key, mode },
    });
    return response.data;
  },

  async streamPdf(key: string): Promise<ArrayBuffer> {
    const response = await apiClient.get<ArrayBuffer>("/courses/pdf-stream", {
      params: { key },
      responseType: "arraybuffer",
    });
    return response.data;
  },
};

export default coursesService;
